import { CurrencyData } from "../global/types";
import store from "../store";

export const CALCULATION_CURRENCY = 'USD';


export const getCurrencyData = (language: string, code: string, rate: number = 1): CurrencyData => {
    // Use 'en-US' locale to get the correct currency symbol regardless of user's language
    // This ensures USD shows as "$" not "RUB" when language is Russian
    const symbol = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: code,
        currencyDisplay: "symbol"
    }).format(0).replace(/[0.,]/g, '').trim();

    return ({
        code,
        name: new Intl.DisplayNames([language], { type: 'currency' }).of(code),
        symbol,
        formatMoney: (value: number, convert: boolean = true) => {
            // Use 'en-US' for consistent currency display across all languages
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: code,
                currencyDisplay: "symbol"
            }).format(value * (convert ? rate : 1));
        }
    } as CurrencyData);
}




let currentCachedKey: string | undefined;
let cachedCurrencies: CurrencyData[] | undefined;

export const getCurrenciesData = async (language?: string, convertFrom: string = CALCULATION_CURRENCY, convertTo: string = CALCULATION_CURRENCY): Promise<CurrencyData[]> => {
    language = language || 'en-US';
    const rate = await getExchangeRate(convertFrom, convertTo);

    const cachedKey = `currencies-${language}-${convertFrom}-${convertTo}`;
    if (currentCachedKey === cachedKey && cachedCurrencies) {
        return cachedCurrencies;
    }

    const currencies = Intl.supportedValuesOf('currency').map(code => {
        return getCurrencyData(language, code, rate);
    });

    cachedCurrencies = currencies.sort((a, b) => a.name.localeCompare(b.name, language));
    currentCachedKey = cachedKey;
    return cachedCurrencies;
};


export const getExchangeRate = async (from: string, to: string): Promise<number> => {
    if (from === to) return 1;

    const cacheKey = `exchange-rate-${from}`;
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
        try {
            const data = JSON.parse(cached);
            const now = new Date();
            const nextUpdate = new Date(data.time_next_update_utc);
            if (now < nextUpdate && data.rates && data.rates[to]) {
                return data.rates[to];
            }
        } catch {
            console.error("Failed to parse cached exchange rate data");
            localStorage.removeItem(cacheKey);
        }
    }

    const response = await fetch(`https://open.er-api.com/v6/latest/${from}`);
    if (!response.ok) throw new Error("Failed to fetch exchange rates");
    const data = await response.json();

    localStorage.setItem(cacheKey, JSON.stringify(data));

    if (data.rates && data.rates[to]) {
        return data.rates[to];
    }
    throw new Error(`Exchange rate for ${to} not found`);
};

export const convertAmount = async (amount: number, from: string, to: string): Promise<number> => {
    if (from === to) return amount;
    const rate = await getExchangeRate(from, to);
    return amount * rate;
}

export function formatCurrencyDisplay(amount: number, currency: string): string {
    try {
        return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency,
        }).format(amount);
    } catch (e) {
        return `${amount} ${currency}`;
    }
}

export function parseUnknowCurrency(str: string) {
    if (!str) return NaN;

    let cleaned = str.replace(/[^\d.,-]/g, '').trim();

    const commaCount = (cleaned.match(/,/g) || []).length;
    const dotCount = (cleaned.match(/\./g) || []).length;

    if (commaCount > 0 && dotCount === 0) {
        cleaned = cleaned.replace(/\s/g, '').replace(',', '.');
    }

    else if (dotCount > 0 && commaCount > 0) {
        cleaned = cleaned.replace(/,/g, '');
    }

    else {
        cleaned = cleaned.replace(/\s/g, '');
    }

    const number = parseFloat(cleaned);
    return isNaN(number) ? NaN : number;
}
